// src/feature-module/settings/Dashboard/Dashboard.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import apiDefinitions from "../../../core/api/apiDefinitions";
import ViewUsersPieChartModule from "./ViewUsersPieChartModule";
import VehicleBooking from "./VehculeBooking";
import KeyAllocationModule from "./KeyAllocationModule";
import VisitorRequest from "./VisitorRequest";
import JobCard from "./JobCard";

const Dashboard: React.FC = () => {
  const [username, setUsername] = useState<string>("Staff Member");
  const [group, setGroup] = useState<string>("KIU Portal");

  // Top KPI state
  const [kpiLoading, setKpiLoading] = useState<boolean>(true);
  const [activeVisitors, setActiveVisitors] = useState<number>(0);
  const [totalVehiclesBooked, setTotalVehiclesBooked] = useState<number>(0);
  const [keysInUse, setKeysInUse] = useState<number>(0);
  const [activeJobs, setActiveJobs] = useState<number>(0);

  useEffect(() => {
    const storedUser = localStorage.getItem("username") || sessionStorage.getItem("username");
    const storedGroup = localStorage.getItem("group") || sessionStorage.getItem("group");
    if (storedUser) setUsername(storedUser);
    if (storedGroup) setGroup(storedGroup);

    // Fetch quick summary stats for top KPI cards
    const fetchTopKPIs = async () => {
      try {
        const [visitorRes, bookingRes, keyRes, jobRes] = await Promise.allSettled([
          apiDefinitions.getVisitorRequestCounts(),
          apiDefinitions.getBookingDashboardCounts(),
          apiDefinitions.getAllocatedKeyCounts(),
          apiDefinitions.getDailyJobCardSummary(),
        ]);

        if (visitorRes.status === "fulfilled" && visitorRes.value?.data?.status === "S1000") {
          setActiveVisitors(visitorRes.value.data.data?.visitorsInside || 0);
        }

        if (bookingRes.status === "fulfilled" && bookingRes.value?.data?.status === "S1000") {
          setTotalVehiclesBooked(bookingRes.value.data.data?.totalBookings || 0);
        }

        if (keyRes.status === "fulfilled" && keyRes.value?.data?.status === "S1000") {
          setKeysInUse(keyRes.value.data.data?.issuedKeyCount || 0);
        }

        if (jobRes.status === "fulfilled" && jobRes.value?.data?.status === "S1000") {
          const daily = jobRes.value.data.data?.dailySummaryData?.[0];
          setActiveJobs((daily?.pending || 0) + (daily?.ongoing || 0));
        }
      } catch (err) {
        console.error("Error fetching top KPI summary:", err);
      } finally {
        setKpiLoading(false);
      }
    };

    fetchTopKPIs();
  }, []);

  // Format today's date
  const todayFormatted = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());

  // Dynamic greeting based on current hour
  const currentHour = new Date().getHours();
  const greeting =
    currentHour < 12 ? "Good Morning" : currentHour < 17 ? "Good Afternoon" : "Good Evening";

  return (
    <div className="page-wrapper">
      <div className="content p-3 p-md-4">
        {/* =========================================================
            1. MODERN GLASSMORPHIC HERO WELCOME BANNER
           ========================================================= */}
        <div className="rm-hero-banner mb-4">
          <div className="row align-items-center g-3">
            <div className="col-12 col-lg-7">
              <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <span className="badge rm-hero-badge px-3 py-1 rounded-pill">
                  <i className="ti ti-shield-check me-1" />
                  {group}
                </span>
                <span className="small rm-hero-date">• {todayFormatted}</span>
              </div>

              <h2 className="rm-hero-title" style={{ color: "#FFFFFF" }}>
                {greeting}, {username} 👋
              </h2>
              <p className="rm-hero-subtitle m-0">
                Welcome to the KIU Resource Management & Operations Control Hub.
              </p>
            </div>

            {/* Quick Action Shortcuts */}
            <div className="col-12 col-lg-5">
              <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                <Link to={all_routes.newBooking} className="rm-action-pill">
                  <i className="ti ti-car" />
                  <span>Book Vehicle</span>
                </Link>

                <Link to={all_routes.visitorRequest} className="rm-action-pill">
                  <i className="ti ti-user-plus" />
                  <span>Visitor Pass</span>
                </Link>

                <Link to={all_routes.keyrequest} className="rm-action-pill">
                  <i className="ti ti-key" />
                  <span>Key Request</span>
                </Link>

                <Link to={all_routes.GateController} className="rm-action-pill">
                  <i className="ti ti-door" />
                  <span>Gate Control</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================
            2. TOP EXECUTIVE KPI STAT CARDS (4 Sleek Cards)
           ========================================================= */}
        <div className="row g-3 mb-4">
          {/* KPI 1: Vehicles */}
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="rm-kpi-card">
              <div>
                <div className="rm-kpi-label">Vehicle Trips</div>
                <div className="rm-kpi-val text-primary">
                  {kpiLoading ? "..." : totalVehiclesBooked}
                </div>
                <div className="rm-kpi-sub">Total Bookings Tracked</div>
              </div>
              <div
                className="rm-kpi-icon-wrap"
                style={{
                  background: "var(--rm-primary-light)",
                  color: "var(--rm-primary)",
                }}
              >
                <i className="ti ti-car" />
              </div>
            </div>
          </div>

          {/* KPI 2: Visitors on Campus */}
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="rm-kpi-card">
              <div>
                <div className="rm-kpi-label d-flex align-items-center gap-1">
                  <span>Inside Campus</span>
                  <span
                    className="spinner-grow spinner-grow-sm text-success"
                    style={{ width: "8px", height: "8px" }}
                    role="status"
                  />
                </div>
                <div className="rm-kpi-val text-success">
                  {kpiLoading ? "..." : activeVisitors}
                </div>
                <div className="rm-kpi-sub">Active Visitors Right Now</div>
              </div>
              <div
                className="rm-kpi-icon-wrap"
                style={{
                  background: "var(--rm-success-light)",
                  color: "var(--rm-success)",
                }}
              >
                <i className="ti ti-users" />
              </div>
            </div>
          </div>

          {/* KPI 3: Keys in Circulation */}
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="rm-kpi-card">
              <div>
                <div className="rm-kpi-label">Keys in Use</div>
                <div className="rm-kpi-val text-warning">
                  {kpiLoading ? "..." : keysInUse}
                </div>
                <div className="rm-kpi-sub">Active Keys Handed Out</div>
              </div>
              <div
                className="rm-kpi-icon-wrap"
                style={{
                  background: "var(--rm-warning-light)",
                  color: "var(--rm-warning)",
                }}
              >
                <i className="ti ti-key" />
              </div>
            </div>
          </div>

          {/* KPI 4: Active Maintenance */}
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="rm-kpi-card">
              <div>
                <div className="rm-kpi-label">Open Job Cards</div>
                <div className="rm-kpi-val" style={{ color: "var(--rm-accent-purple)" }}>
                  {kpiLoading ? "..." : activeJobs}
                </div>
                <div className="rm-kpi-sub">Pending / In Progress</div>
              </div>
              <div
                className="rm-kpi-icon-wrap"
                style={{
                  background: "rgba(139, 92, 246, 0.12)",
                  color: "var(--rm-accent-purple)",
                }}
              >
                <i className="ti ti-briefcase" />
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================
            3. MODERN INTERACTIVE CHARTS & ANALYTICS GRID
           ========================================================= */}
        <div className="row g-3 g-md-4">
          {/* Row 1: Vehicle Bookings & User Pie Chart */}
          <div className="col-12 col-lg-6 d-flex">
            <VehicleBooking />
          </div>

          <div className="col-12 col-lg-6 d-flex">
            <ViewUsersPieChartModule />
          </div>

          {/* Row 2: Key Allocation Summary (Full Width) */}
          <div className="col-12 d-flex">
            <KeyAllocationModule />
          </div>

          {/* Row 3: Visitor Requests & Daily Job Cards */}
          <div className="col-12 col-lg-6 d-flex">
            <VisitorRequest />
          </div>

          <div className="col-12 col-lg-6 d-flex">
            <JobCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
